import { TABLE_SETTINGS_DIALOG_DELAY_MS } from '../../api/common';

function cleanAlignmentValue(alignment: string | null | undefined): string | null {
    if (!alignment) return null;
    
    const validPattern = /^(top|middle|bottom)-(left|center|right)$/;
    if (validPattern.test(alignment)) {
        return alignment;
    }
    
    const matches = alignment.match(/(top|middle|bottom)-(left|center|right)/g);
    if (matches && matches.length > 0) {
        return matches[matches.length - 1];
    }
    
    const valignMatch = alignment.match(/(top|middle|bottom)/);
    const alignMatch = alignment.match(/(left|center|right)/);
    if (valignMatch && alignMatch) {
        return `${valignMatch[1]}-${alignMatch[1]}`;
    }
    
    return 'top-left';
}

function rebuildStyleAttribute(element: HTMLElement, additionalStyles: Record<string, string> = {}): void {
    const styles: string[] = [];
    
    for (let i = 0; i < element.style.length; i++) {
        const prop = element.style[i];
        const value = element.style.getPropertyValue(prop);
        if (value) {
            styles.push(`${prop}: ${value}`);
        }
    }
    
    Object.entries(additionalStyles).forEach(([prop, value]) => {
        const index = styles.findIndex(s => s.startsWith(`${prop}:`));
        if (index >= 0) {
            styles[index] = `${prop}: ${value}`;
        } else {
            styles.push(`${prop}: ${value}`);
        }
    });
    
    if (styles.length > 0) {
        element.setAttribute('style', styles.join('; '));
    } else {
        element.removeAttribute('style');
    }
}

export function createTableSettingsDialogPlugin(): any {
    return {
        name: 'tableSettingsDialog',
        display: 'dialog',
        title: "Table and Cell Settings",
        innerHTML: `<div className='header-button'>Table Settings</div>`,
        buttonClass: "header-button-wrapper",

        add: function (core: any) {
            core.addModule(['dialog']);

            const context = core.context as any;
            context.tableSettingsDialog = {
                dropdownSelect: null as HTMLSelectElement | null,
                allCellsAlignSelect: null as HTMLSelectElement | null,
                cellAlignSelect: null as HTMLSelectElement | null,
                tableBorderCheckbox: null as HTMLInputElement | null,
                tableNoOutsideBorderCheckbox: null as HTMLInputElement | null,
                cellBorderCheckbox: null as HTMLInputElement | null,
                cellBorderTopCheckbox: null as HTMLInputElement | null,
                cellBorderRightCheckbox: null as HTMLInputElement | null,
                cellBorderBottomCheckbox: null as HTMLInputElement | null,
                cellBorderLeftCheckbox: null as HTMLInputElement | null,
                tableWidthInput: null as HTMLInputElement | null,
                cellPaddingInput: null as HTMLInputElement | null,
                activeTab: 'table-settings',
                printLandscapeCheckbox: null as HTMLInputElement | null,
            };

            let dropdown_dialog = this.setDialog(core);
            context.tableSettingsDialog.modal = dropdown_dialog;
            context.tableSettingsDialog.dropdownSelect = dropdown_dialog.querySelector('.se-input-select');
            context.tableSettingsDialog.allCellsAlignSelect = dropdown_dialog.querySelector('.se-all-cells-align-select');
            context.tableSettingsDialog.cellAlignSelect = dropdown_dialog.querySelector('.se-cell-align-select');
            context.tableSettingsDialog.tableBorderCheckbox = dropdown_dialog.querySelector('.se-table-border-toggle');
            context.tableSettingsDialog.tableNoOutsideBorderCheckbox = dropdown_dialog.querySelector('.se-table-no-outside-border-toggle');
            context.tableSettingsDialog.cellBorderCheckbox = dropdown_dialog.querySelector('.se-cell-border-toggle');
            context.tableSettingsDialog.cellBorderTopCheckbox = dropdown_dialog.querySelector('.se-cell-border-top-toggle');
            context.tableSettingsDialog.cellBorderRightCheckbox = dropdown_dialog.querySelector('.se-cell-border-right-toggle');
            context.tableSettingsDialog.cellBorderBottomCheckbox = dropdown_dialog.querySelector('.se-cell-border-bottom-toggle');
            context.tableSettingsDialog.cellBorderLeftCheckbox = dropdown_dialog.querySelector('.se-cell-border-left-toggle');
            context.tableSettingsDialog.tableWidthInput = dropdown_dialog.querySelector('.se-table-width-input');
            context.tableSettingsDialog.cellPaddingInput = dropdown_dialog.querySelector('.se-cell-padding-input');
            context.tableSettingsDialog.printLandscapeCheckbox = dropdown_dialog.querySelector('.se-print-landscape-toggle');

            const form = dropdown_dialog.querySelector('form.editor_dropdown');
            if (form) {
                form.addEventListener('submit', this.submit.bind(core));
            }
            const tabButtons = dropdown_dialog.querySelectorAll(".se-tab-button");
            tabButtons.forEach((button: any) => {
                button.addEventListener("click", () => {
                    context.tableSettingsDialog.activeTab = button.getAttribute("data-tab");
                    tabButtons.forEach((btn: any) => btn.classList.remove("active"));
                    button.classList.add("active");
                });
            });

            const landscapeCheckbox = context.tableSettingsDialog.printLandscapeCheckbox;
            const widthInput = context.tableSettingsDialog.tableWidthInput;
            if (landscapeCheckbox && widthInput) {
                landscapeCheckbox.addEventListener('change', function() {
                    const isLandscape = landscapeCheckbox.checked;
                    const currentWidth = parseInt(widthInput.value || '366');
                    if (isLandscape) {
                        widthInput.setAttribute('max', '612');
                        if (currentWidth === 366) {
                            widthInput.value = '612';
                        }
                    } else {
                        widthInput.setAttribute('max', '366');
                        if (currentWidth === 612) {
                            widthInput.value = '366';
                        }
                    }
                });
            }

            context.dialog.modal.appendChild(dropdown_dialog);
        },

        setDialog: function (core: any): HTMLDivElement {
            const dialog = document.createElement("div");
            dialog.className = "se-dialog-content";
            dialog.style.display = "none";
            dialog.style.position = "absolute";
            dialog.style.width = "500px";
            dialog.style.top = "50px";
            dialog.style.left = "calc(50% - 250px)";

            let html = `
                <form class="editor_dropdown">
                    <div class="se-dialog-header" id="se-dialog-header" style="cursor: move;">
                        <button type="button" data-command="close" class="se-btn se-dialog-close" aria-label="Close">
                            ${core.icons.cancel}
                        </button>
                        <span class="se-modal-title">Table & Cell Settings</span>
                    </div>

                    <!-- Tab Navigation -->
                    <div class="se-dialog-tabs">
                        <button type="button" class="se-tab-button active" data-tab="table-settings">Table Settings</button>
                        <button type="button" class="se-tab-button" data-tab="cell-settings">Cell Settings</button>
                    </div>

                    <div class="se-dialog-body">
                        <!-- Table Settings Tab -->
                        <div class="se-tab-content" id="table-settings">
                            <div class="se-dialog-grid">
                                <div class="se-dialog-form">
                                    <label>Table Width (pts)</label>
                                    <input type="number" class="se-table-width-input" min="72" max="366" value="366">
                                </div>

                                <div class="se-dialog-form">
                                    <label>Table Alignment</label>
                                    <select class="se-input-select">
                                        <option value="none">None</option>
                                        <option value="left">Left</option>
                                        <option value="center">Center</option>
                                        <option value="right">Right</option>
                                    </select>
                                </div>
                            </div>

                            <div class="se-dialog-form">
                                <strong>All Table Cells</strong>
                            </div>

                            <div class="se-dialog-grid">
                                <div class="se-dialog-form">
                                    <label>Cell Padding (px)</label>
                                    <input type="number" class="se-cell-padding-input" min="0" max="50" value="5">
                                </div>

                                <div class="se-dialog-form">
                                    <label>Text Alignment</label>
                                    <select class="se-all-cells-align-select">
                                        <option value="top-left">Top Left</option>
                                        <option value="top-center">Top Center</option>
                                        <option value="top-right">Top Right</option>
                                        <option value="middle-left">Center Left</option>
                                        <option value="middle-center">Center</option>
                                        <option value="middle-right">Center Right</option>
                                        <option value="bottom-left">Bottom Left</option>
                                        <option value="bottom-center">Bottom Center</option>
                                        <option value="bottom-right">Bottom Right</option>
                                    </select>
                                </div>

                                <div class="se-checkbox-group">
                                    <label>
                                        <input type="checkbox" class="se-table-border-toggle">
                                        Hide All Borders
                                    </label>
                                </div>

                                <div class="se-checkbox-group">
                                    <label>
                                        <input type="checkbox" class="se-table-no-outside-border-toggle">
                                        Hide Outside Borders
                                    </label>
                                </div>

                                <div class="se-checkbox-group">
                                <label>
                                    <input type="checkbox" class="se-print-landscape-toggle">
                                    Print Landscape
                                </label>
                                </div>
                            </div>
                        </div>

                        <!-- Cell Settings Tab -->
                        <div class="se-tab-content" id="cell-settings" style="display: none;">
                            <div class="se-dialog-grid">
                                <div class="se-dialog-form">
                                    <label>Text Alignment</label>
                                    <select class="se-cell-align-select">
                                        <option value="top-left">Top Left</option>
                                        <option value="top-center">Top Center</option>
                                        <option value="top-right">Top Right</option>
                                        <option value="middle-left">Center Left</option>
                                        <option value="middle-center">Center</option>
                                        <option value="middle-right">Center Right</option>
                                        <option value="bottom-left">Bottom Left</option>
                                        <option value="bottom-center">Bottom Center</option>
                                        <option value="bottom-right">Bottom Right</option>
                                    </select>
                                </div>

                                <div class="se-checkbox-group">
                                    <label>
                                        <input type="checkbox" class="se-cell-border-toggle">
                                        Hide All Cell Borders
                                    </label>
                                </div>
                            </div>

                            <div class="se-dialog-form">
                                <strong>Individual Border Controls</strong>
                            </div>

                            <div class="se-dialog-grid">
                                <div class="se-checkbox-group">
                                    <label>
                                        <input type="checkbox" class="se-cell-border-top-toggle">
                                        Hide Top Border
                                    </label>
                                </div>

                                <div class="se-checkbox-group">
                                    <label>
                                        <input type="checkbox" class="se-cell-border-right-toggle">
                                        Hide Right Border
                                    </label>
                                </div>

                                <div class="se-checkbox-group">
                                    <label>
                                        <input type="checkbox" class="se-cell-border-bottom-toggle">
                                        Hide Bottom Border
                                    </label>
                                </div>

                                <div class="se-checkbox-group">
                                    <label>
                                        <input type="checkbox" class="se-cell-border-left-toggle">
                                        Hide Left Border
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="se-dialog-footer">
                        <button type="submit" class="se-btn-primary">
                            <span>Apply</span>
                        </button>
                    </div>
                </form>
            `;

            dialog.innerHTML = html;

            const header = dialog.querySelector("#se-dialog-header") as HTMLDivElement;
            let offsetX = 0, offsetY = 0, isDragging = false;

            function onMouseMove(event: MouseEvent) {
                if (isDragging) {
                    const viewportWidth = window.innerWidth;
                    const viewportHeight = window.innerHeight;
                    const dialogWidth = dialog.offsetWidth;
                    const dialogHeight = dialog.offsetHeight;

                    let newX = event.clientX - offsetX;
                    let newY = event.clientY - offsetY;

                    // Prevent moving outside the viewport
                    newX = Math.max(0, Math.min(viewportWidth - dialogWidth, newX));
                    newY = Math.max(0, Math.min(viewportHeight - dialogHeight, newY));

                    dialog.style.left = `${newX}px`;
                    dialog.style.top = `${newY}px`;
                }
            }

            function onMouseUp() {
                isDragging = false;
                document.removeEventListener("mousemove", onMouseMove);
                document.removeEventListener("mouseup", onMouseUp);
            }

            header?.addEventListener("mousedown", function (event) {
                isDragging = true;
                offsetX = event.clientX - dialog.offsetLeft;
                offsetY = event.clientY - dialog.offsetTop;
                dialog.style.zIndex = "9999";

                document.addEventListener("mousemove", onMouseMove);
                document.addEventListener("mouseup", onMouseUp);
            });

            const tabButtons = dialog.querySelectorAll(".se-tab-button");
            const tabContents = dialog.querySelectorAll(".se-tab-content");

            tabButtons.forEach((button) => {
                button.addEventListener("click", function () {
                    tabButtons.forEach((btn) => btn.classList.remove("active"));
                    button.classList.add("active");

                    tabContents.forEach((content) => {
                        (content as HTMLElement).style.display = "none";
                    });

                    const selectedTab = button.getAttribute("data-tab");
                    const selectedContent = dialog.querySelector(`#${selectedTab}`) as HTMLElement;
                    if (selectedContent) {
                        selectedContent.style.display = "block";
                    }
                });
            });

            return dialog;
        },

        open: function (this: any) {
            const selection = this.getSelection();
            if (!selection || !selection.rangeCount) {
                this.context.tableSettingsDialog.selectedTable = null;
                this.context.tableSettingsDialog.selectedCell = null;
                return;
            }

            const range = selection.getRangeAt(0);
            let selectedNode = range.startContainer as HTMLElement;

            if (selectedNode.nodeType === 3) { // Text node
                selectedNode = selectedNode.parentElement as HTMLElement;
            }

            const cell = selectedNode?.closest('td, th') as HTMLElement;
            const table = cell?.closest('table') as HTMLElement;

            if (table) {
                this.context.tableSettingsDialog.selectedTable = table;
                this.context.tableSettingsDialog.printLandscapeCheckbox!.checked = this.context.tableSettingsDialog.selectedTable?.hasAttribute("data-print-landscape") || false;
                const isLandscape = table.hasAttribute('data-print-landscape');
                const defaultWidth = isLandscape ? 612 : 366;
                const tableWidth = table.style.width ? parseInt(table.style.width.replace('pt', '').replace('%', '')) : defaultWidth;
                this.context.tableSettingsDialog.tableWidthInput!.value = tableWidth.toString();
                this.context.tableSettingsDialog.tableWidthInput!.setAttribute('max', isLandscape ? '612' : '366');

                let alignment = "none";
                if (table.style.marginLeft === '0px' && table.style.marginRight === 'auto') alignment = "left";
                else if (table.style.marginLeft === 'auto' && table.style.marginRight === 'auto') alignment = "center";
                else if (table.style.marginLeft === 'auto' && table.style.marginRight === '0px') alignment = "right";
                this.context.tableSettingsDialog.dropdownSelect!.value = alignment;

                this.context.tableSettingsDialog.tableBorderCheckbox!.checked = table.classList.contains("se-table-no-border");
                this.context.tableSettingsDialog.tableNoOutsideBorderCheckbox!.checked = table.classList.contains("se-table-no-outside-border");

                const firstCell = table.querySelector("td, th") as HTMLElement;
                const cellPadding = firstCell?.style.padding ? parseInt(firstCell.style.padding.replace('px', '')) : 5;
                this.context.tableSettingsDialog.cellPaddingInput!.value = cellPadding.toString();
            } else {
                this.context.tableSettingsDialog.selectedTable = null;
                this.context.tableSettingsDialog.tableWidthInput!.value = "366";
                this.context.tableSettingsDialog.tableWidthInput!.setAttribute('max', '366');
                this.context.tableSettingsDialog.cellPaddingInput!.value = "5";
                this.context.tableSettingsDialog.dropdownSelect!.value = "none";
                this.context.tableSettingsDialog.tableBorderCheckbox!.checked = false;
                this.context.tableSettingsDialog.tableNoOutsideBorderCheckbox!.checked = false;
                this.context.tableSettingsDialog.printLandscapeCheckbox!.checked = false;
            }

            if (cell) {
                this.context.tableSettingsDialog.selectedCell = cell;

                const innerElement = cell.querySelector("p, div") as HTMLElement | null;
                const allCellsAlignmentRaw = innerElement?.getAttribute('data-align');
                const cellAlignmentRaw = innerElement?.getAttribute('data-cell-align') || allCellsAlignmentRaw;
                
                const allCellsAlignment = cleanAlignmentValue(allCellsAlignmentRaw) || 'top-left';
                const cellAlignment = cleanAlignmentValue(cellAlignmentRaw) || allCellsAlignment;
                
                this.context.tableSettingsDialog.cellAlignSelect!.value = cellAlignment;
                this.context.tableSettingsDialog.allCellsAlignSelect!.value = allCellsAlignment;
                this.context.tableSettingsDialog.cellBorderCheckbox!.checked = cell.classList.contains("se-cell-no-border");

                this.context.tableSettingsDialog.cellBorderTopCheckbox!.checked = cell.classList.contains("se-cell-no-border-top");
                this.context.tableSettingsDialog.cellBorderRightCheckbox!.checked = cell.classList.contains("se-cell-no-border-right");
                this.context.tableSettingsDialog.cellBorderBottomCheckbox!.checked = cell.classList.contains("se-cell-no-border-bottom");
                this.context.tableSettingsDialog.cellBorderLeftCheckbox!.checked = cell.classList.contains("se-cell-no-border-left");
            } else {
                this.context.tableSettingsDialog.selectedCell = null;
                this.context.tableSettingsDialog.allCellsAlignSelect!.value = "top-left";
                this.context.tableSettingsDialog.cellAlignSelect!.value = "top-left";
                this.context.tableSettingsDialog.cellBorderCheckbox!.checked = false;
                this.context.tableSettingsDialog.cellBorderTopCheckbox!.checked = false;
                this.context.tableSettingsDialog.cellBorderRightCheckbox!.checked = false;
                this.context.tableSettingsDialog.cellBorderBottomCheckbox!.checked = false;
                this.context.tableSettingsDialog.cellBorderLeftCheckbox!.checked = false;
            }

            this.plugins.dialog.open.call(this, 'tableSettingsDialog');

            setTimeout(() => {
                const firstInput = this.context.tableSettingsDialog.modal?.querySelector("input, select, button") as HTMLElement;
                if (firstInput) {
                    firstInput.focus();
                }
            }, TABLE_SETTINGS_DIALOG_DELAY_MS);
        },

        submit: function (this: any, e: Event) {
            e.preventDefault();
            e.stopPropagation();
            const contextDropdown = this.context.tableSettingsDialog;
            const selectedTable = contextDropdown.selectedTable;
            const selectedCell = contextDropdown.selectedCell;
            const selectedAlignment = contextDropdown.dropdownSelect?.value;
            const selectedCellsAlignment = contextDropdown.allCellsAlignSelect?.value;
            const selectedCellAlignment = contextDropdown.cellAlignSelect?.value;
            const tableBorderEnabled = contextDropdown.tableBorderCheckbox?.checked;
            const tableNoOutsideBorderEnabled = contextDropdown.tableNoOutsideBorderCheckbox?.checked;
            const cellBorderEnabled = contextDropdown.cellBorderCheckbox?.checked;
            const cellBorderTopEnabled = contextDropdown.cellBorderTopCheckbox?.checked;
            const cellBorderRightEnabled = contextDropdown.cellBorderRightCheckbox?.checked;
            const cellBorderBottomEnabled = contextDropdown.cellBorderBottomCheckbox?.checked;
            const cellBorderLeftEnabled = contextDropdown.cellBorderLeftCheckbox?.checked;
            const printLandscapeEnabled = contextDropdown.printLandscapeCheckbox?.checked;
            const defaultWidth = printLandscapeEnabled ? "612" : "366";
            const tableWidth = contextDropdown.tableWidthInput?.value || defaultWidth;
            const cellPadding = contextDropdown.cellPaddingInput?.value || "5"
            const tableTabActive = contextDropdown.activeTab === 'table-settings';
            const cellTabActive = contextDropdown.activeTab === 'cell-settings';

            function getAlignmentValues(combo: string): { textAlign: string; verticalAlign: string } {
                const cleanedCombo = cleanAlignmentValue(combo) || 'top-left';
                const [vertical, horizontal] = cleanedCombo.split('-');

                const verticalAlign = {
                    top: 'top',
                    middle: 'middle',
                    bottom: 'bottom'
                }[vertical] || 'top';

                const textAlign = {
                    left: 'left',
                    center: 'center',
                    right: 'right'
                }[horizontal] || 'left';

                return { textAlign, verticalAlign };
            }

            if (!selectedTable) {
                this.plugins.dialog.close.call(this);
                return;
            }

            if (selectedTable instanceof HTMLElement && selectedTable?.tagName === 'TABLE') {

                if (tableTabActive) {
                    const actualTableWidth = Math.min(612, Math.max(72, parseInt(tableWidth)));
                    selectedTable.setAttribute("style", `width: ${actualTableWidth}pt !important;`);
                    let existingColgroup = selectedTable.querySelector('colgroup');
                    const firstRow = selectedTable.querySelector('tr');
                    
                    if (firstRow) {
                        const cells = firstRow.querySelectorAll('td, th');
                        if (cells.length > 0) {
                            let columnCount = 0;
                            const cellSpans: number[] = [];
                            cells.forEach((cell) => {
                                const colspan = parseInt(cell.getAttribute('colspan') || '1');
                                cellSpans.push(colspan);
                                columnCount += colspan;
                            });

                            if (columnCount > 0) {
                                if (existingColgroup) {
                                    existingColgroup.remove();
                                }

                                const colgroup = document.createElement('colgroup');
                                const singleColumnWidth = Math.floor(actualTableWidth / columnCount);
                                
                                cellSpans.forEach((span) => {
                                    const col = document.createElement('col');
                                    const colWidth = singleColumnWidth * span;
                                    col.setAttribute('style', `width: ${colWidth}pt;`);
                                    
                                    if (span > 1) {
                                        col.setAttribute('span', span.toString());
                                    }
                                    
                                    colgroup.appendChild(col);
                                });

                                if (selectedTable.firstChild) {
                                    selectedTable.insertBefore(colgroup, selectedTable.firstChild);
                                } else {
                                    selectedTable.appendChild(colgroup);
                                }
                            }
                        }
                    }

                    switch (selectedAlignment) {
                        case 'left':
                            selectedTable.style.marginLeft = '0';
                            selectedTable.style.marginRight = 'auto';
                            break;
                        case 'center':
                            selectedTable.style.marginLeft = 'auto';
                            selectedTable.style.marginRight = 'auto';
                            break;
                        case 'right':
                            selectedTable.style.marginLeft = 'auto';
                            selectedTable.style.marginRight = '0';
                            break;
                        default:
                            selectedTable.style.marginLeft = '';
                            selectedTable.style.marginRight = '';
                            break;
                    }

                    selectedTable.classList.toggle("se-table-no-border", tableBorderEnabled);
                    selectedTable.classList.toggle("se-table-no-outside-border", tableNoOutsideBorderEnabled);

                    const cells = selectedTable.querySelectorAll("td, th");
                    cells.forEach((cell) => {
                        if (cell instanceof HTMLElement) {
                            const { textAlign, verticalAlign } = getAlignmentValues(selectedCellsAlignment);

                            cell.removeAttribute('valign');
                            
                            const paddingValue = `${Math.min(50, Math.max(0, parseInt(cellPadding)))}px`;
                            cell.style.padding = paddingValue;
                            cell.style.verticalAlign = verticalAlign;
                            
                            if (cell.tagName === 'TH') {
                                const styles: string[] = [];
                                for (let i = 0; i < cell.style.length; i++) {
                                    const prop = cell.style[i];
                                    const value = cell.style.getPropertyValue(prop);
                                    if (value) {
                                        styles.push(`${prop}: ${value}`);
                                    }
                                }
                                const existingVAlignIndex = styles.findIndex(s => s.startsWith('vertical-align:'));
                                if (existingVAlignIndex >= 0) {
                                    styles[existingVAlignIndex] = `vertical-align: ${verticalAlign}`;
                                } else {
                                    styles.push(`vertical-align: ${verticalAlign}`);
                                }
                                const existingPaddingIndex = styles.findIndex(s => s.startsWith('padding:'));
                                if (existingPaddingIndex >= 0) {
                                    styles[existingPaddingIndex] = `padding: ${paddingValue}`;
                                } else {
                                    styles.push(`padding: ${paddingValue}`);
                                }
                                cell.setAttribute('style', styles.join('; '));
                            } else {
                                rebuildStyleAttribute(cell, { 
                                    'vertical-align': verticalAlign,
                                    'padding': paddingValue
                                });
                            }
                            
                            void cell.offsetHeight;
                            
                            const innerElement = cell.querySelector("p, div") as HTMLElement | null;
                            if (innerElement && selectedCellsAlignment) {
                                innerElement.setAttribute('data-cell-align', selectedCellsAlignment);
                                innerElement.setAttribute('data-align', selectedCellsAlignment);
                                const existingInnerStyle = innerElement.getAttribute('style') || '';
                                const styleWithoutTextAlign = existingInnerStyle.replace(/text-align\s*:\s*[^;]+;?/gi, '').trim();
                                const newInnerStyle = styleWithoutTextAlign 
                                    ? `${styleWithoutTextAlign}; text-align: ${textAlign}`.replace(/;+/g, ';').replace(/^;|;$/g, '').trim()
                                    : `text-align: ${textAlign}`;
                                innerElement.setAttribute('style', newInnerStyle);
                                innerElement.style.textAlign = textAlign;
                            }
                        }
                    });
                }

                const printLandscapeEnabled = contextDropdown.printLandscapeCheckbox?.checked;

                selectedTable.toggleAttribute("data-print-landscape", printLandscapeEnabled);

                let iconEl = selectedTable.previousElementSibling;
                const isLandscapeIcon = iconEl?.getAttribute("data-landscape-icon") === "true";
                if (printLandscapeEnabled) {
                    if (!iconEl || !isLandscapeIcon) {
                        iconEl = document.createElement("div");
                        iconEl.textContent = "🔄";
                        iconEl.setAttribute("data-landscape-icon", "true");
                        selectedTable.parentNode?.insertBefore(iconEl, selectedTable);
                    }
                } else {
                    if (iconEl && isLandscapeIcon) {
                        iconEl.remove();
                    }
                }

                if (cellTabActive && selectedCell && (selectedCell?.tagName === "TD" || selectedCell?.tagName === "TH")) {
                    selectedCell.classList.toggle("se-cell-no-border", cellBorderEnabled);

                    selectedCell.classList.toggle("se-cell-no-border-top", cellBorderTopEnabled);
                    selectedCell.classList.toggle("se-cell-no-border-right", cellBorderRightEnabled);
                    selectedCell.classList.toggle("se-cell-no-border-bottom", cellBorderBottomEnabled);
                    selectedCell.classList.toggle("se-cell-no-border-left", cellBorderLeftEnabled);

                    const { textAlign, verticalAlign } = getAlignmentValues(selectedCellAlignment);
                    
                    selectedCell.removeAttribute('valign');
                    selectedCell.style.verticalAlign = verticalAlign;
                    
                    if (selectedCell.tagName === 'TH') {
                        const styles: string[] = [];
                        for (let i = 0; i < selectedCell.style.length; i++) {
                            const prop = selectedCell.style[i];
                            const value = selectedCell.style.getPropertyValue(prop);
                            if (value && prop !== 'vertical-align') {
                                styles.push(`${prop}: ${value}`);
                            }
                        }
                        styles.push(`vertical-align: ${verticalAlign}`);
                        
                        const styleString = styles.join('; ');
                        selectedCell.setAttribute('style', styleString);
                        
                        const verifyStyle = selectedCell.getAttribute('style');
                        if (!verifyStyle || !verifyStyle.includes('vertical-align')) {
                            const existing = verifyStyle || '';
                            const cleaned = existing.replace(/vertical-align\s*:\s*[^;]+;?/gi, '').trim();
                            const forced = cleaned ? `${cleaned}; vertical-align: ${verticalAlign}` : `vertical-align: ${verticalAlign}`;
                            selectedCell.setAttribute('style', forced);
                        }
                    } else {
                        rebuildStyleAttribute(selectedCell, { 'vertical-align': verticalAlign });
                    }
                    
                    void selectedCell.offsetHeight;
                    
                    const innerElement = selectedCell.querySelector("p, div") as HTMLElement | null;
                    if (innerElement) {
                        innerElement.setAttribute('data-cell-align', selectedCellAlignment);
                        const existingInnerStyle = innerElement.getAttribute('style') || '';
                        const styleWithoutTextAlign = existingInnerStyle.replace(/text-align\s*:\s*[^;]+;?/gi, '').trim();
                        const newInnerStyle = styleWithoutTextAlign 
                            ? `${styleWithoutTextAlign}; text-align: ${textAlign}`.replace(/;+/g, ';').replace(/^;|;$/g, '').trim()
                            : `text-align: ${textAlign}`;
                        innerElement.setAttribute('style', newInnerStyle);
                        innerElement.style.textAlign = textAlign;
                    }
                }
            }

            this.history.push(true);
            this.plugins.dialog.close.call(this);
            
            if (this.core?.context?.element?.wysiwyg) {
                const event = new Event('input', { bubbles: true });
                this.core.context.element.wysiwyg.dispatchEvent(event);
            }
        },
    };
}
